import { useSyncExternalStore } from 'react';

import {
    readV3GeneralSessionRunId,
    type V3GeneralRunPersistenceScope,
} from '@apps/web/lib/chat_ui_persistence';

type Selection =
    | Readonly<{ view: 'list'; runId: string | null }>
    | Readonly<{ view: 'conversation'; runId: string }>
    | Readonly<{ view: 'new'; runId: null }>;

export type V3GeneralChatNavigation = Selection & Readonly<{ version: 1 }>;

const NEW_CHAT: V3GeneralChatNavigation = {
    version: 1,
    view: 'new',
    runId: null,
};
const STORAGE_PREFIX = 'phoebe-chat-v3-general-selection';

function scopeKey(scope: V3GeneralRunPersistenceScope): string {
    return `${scope.userId}:${scope.organizationId}:${scope.mode}`;
}

function parseSelection(raw: string): V3GeneralChatNavigation {
    try {
        const value: unknown = JSON.parse(raw);
        if (
            typeof value === 'object' &&
            value !== null &&
            'version' in value &&
            value.version === 1
        ) {
            const runId =
                'runId' in value &&
                typeof value.runId === 'string' &&
                value.runId.trim() !== ''
                    ? value.runId
                    : null;
            // Records from before the view was stored carry only a run id;
            // they keep restoring that conversation.
            if ('view' in value && value.view === 'list') {
                return { version: 1, view: 'list', runId };
            }
            if (runId !== null) {
                return { version: 1, view: 'conversation', runId };
            }
        }
    } catch {
        // A damaged preference must not prevent starting a conversation.
    }
    return NEW_CHAT;
}

function persistSelection(key: string, selection: Selection): void {
    const storageKey = `${STORAGE_PREFIX}:${key}`;
    try {
        window.localStorage.setItem(
            storageKey,
            JSON.stringify({
                version: 1,
                view: selection.view,
                runId: selection.runId,
            }),
        );
    } catch {
        try {
            // A quota failure must not restore the previously selected chat.
            window.localStorage.removeItem(storageKey);
        } catch {
            // Page navigation still works when browser storage is unavailable.
        }
    }
    try {
        window.sessionStorage.removeItem(
            `phoebe-chat-v3-general-navigation:${key}`,
        );
        window.sessionStorage.removeItem(
            `phoebe-chat-v3-general-session-run:${key}`,
        );
    } catch {
        // Session storage may be blocked independently of browser preferences.
    }
}

function initialSelection(
    scope: V3GeneralRunPersistenceScope,
): V3GeneralChatNavigation {
    const key = scopeKey(scope);
    try {
        const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${key}`);
        if (raw !== null) {
            return parseSelection(raw);
        }
    } catch {
        // An older session selection may still be readable.
    }
    try {
        const previous = window.sessionStorage.getItem(
            `phoebe-chat-v3-general-navigation:${key}`,
        );
        const legacyRunId = readV3GeneralSessionRunId(scope);
        const migrated =
            previous !== null
                ? parseSelection(previous)
                : legacyRunId
                  ? ({
                        version: 1,
                        view: 'conversation',
                        runId: legacyRunId,
                    } as const)
                  : null;
        if (migrated !== null) {
            persistSelection(key, migrated);
            return migrated;
        }
    } catch {
        // With no readable selection, start a new chat.
    }
    return NEW_CHAT;
}

/** Each page takes an initial browser selection, then owns its navigation. */
export function createV3GeneralChatNavigationStore() {
    const snapshots = new Map<string, V3GeneralChatNavigation>();
    const listeners = new Set<() => void>();
    const notify = () => {
        for (const listener of listeners) {
            listener();
        }
    };
    const read = (
        scope: V3GeneralRunPersistenceScope | null,
    ): V3GeneralChatNavigation => {
        if (scope === null || typeof window === 'undefined') {
            return NEW_CHAT;
        }
        const key = scopeKey(scope);
        const cached = snapshots.get(key);
        if (cached !== undefined) {
            return cached;
        }
        const initial = initialSelection(scope);
        snapshots.set(key, initial);
        return initial;
    };
    const write = (
        scope: V3GeneralRunPersistenceScope,
        selection: Selection,
    ): void => {
        const key = scopeKey(scope);
        snapshots.set(key, { version: 1, ...selection });
        // A refresh restores the screen the reader was on, list included.
        persistSelection(key, selection);
        notify();
    };
    const forget = (
        scope: V3GeneralRunPersistenceScope,
        unavailableRunId: string,
    ): void => {
        const key = scopeKey(scope);
        snapshots.set(key, NEW_CHAT);
        try {
            const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${key}`);
            // Another tab may have selected a valid chat while this one loaded.
            if (
                raw !== null &&
                parseSelection(raw).runId === unavailableRunId
            ) {
                persistSelection(key, NEW_CHAT);
            }
        } catch {
            // The page can still leave the unavailable conversation.
        }
        notify();
    };
    const subscribe = (listener: () => void): (() => void) => {
        listeners.add(listener);
        return () => listeners.delete(listener);
    };
    return { read, write, forget, subscribe };
}

const navigation = createV3GeneralChatNavigationStore();
export const readV3GeneralChatNavigation = navigation.read;
export const writeV3GeneralChatNavigation = navigation.write;
export const forgetV3GeneralChatSelection = navigation.forget;

export function useV3GeneralChatNavigation(
    scope: V3GeneralRunPersistenceScope | null,
): V3GeneralChatNavigation {
    return useSyncExternalStore(
        navigation.subscribe,
        () => navigation.read(scope),
        () => NEW_CHAT,
    );
}
